/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useLabels } from '@/hooks/useLabels';
import styles from './styles.module.scss';
import { Actionable, Icon, Text } from '@az/starc-ui';
import { StarFill } from '@az/starc-ui-icons';
import { FlexGrid, type FlexGridGap, type GapUnit } from '@/components/FlexGrid/FlexGrid';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import cx from 'classnames';
import { useBvPixelTracking } from '@/features/customReviews/hooks/useBvPixelTracking';
import { useReviewFilters } from '@/features/customReviews/context/ReviewFiltersProvider';
import { scrollToCustomerReviews } from '@/features/customReviews/utils/scrollToCustomerReviews';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type RatingDistributionsProps = {
  noReviews?: boolean;
  isPopup?: boolean;
  close?: VoidFunction;
};

const labelMap = {
  ratings: 'label_reviews_ratings',
  star: 'label_customReviews_star',
  stars: 'label_customReviews_stars',
};

const steps = new Array(5).fill(0);

export const RatingDistributions = ({
  noReviews = false,
  isPopup = false,
  close,
}: RatingDistributionsProps) => {
  const labels = useLabels(labelMap);
  const { setSelectedRatings } = useReviewFilters();
  const { trackEvent } = useBvPixelTracking();
  const pdpRedesignEnabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const pdpBottomRedesign = pdpRedesignEnabled && !isPopup;

  const { data: productDetails } = useProductDetails({ enabled: false });
  const product = productDetails?.product;
  const skuNumber = product?.itemId ?? '';
  const { data: reviewStatisticsData } = useReviewStatistics({
    skuNumbers: [skuNumber],
    enabled: Boolean(skuNumber),
  });

  const reviewStatistics = reviewStatisticsData?.[skuNumber];
  const ratings = reviewStatistics?.ratingDistributionValues;

  const handleClick = (star: number) => {
    scrollToCustomerReviews();
    setSelectedRatings([star]);
    trackEvent('Rating Distribution', 'Click', 'Filter', star);
    close?.();
  };

  const gap: FlexGridGap | GapUnit = pdpBottomRedesign ? { sm: 4, md: 5 } : 4;

  if (!ratings) {
    return null;
  }

  return (
    <FlexGrid gap={pdpBottomRedesign ? { sm: 3, md: 4 } : 2} className={styles.ratingsContainer}>
      <FlexGrid className={styles.column} gap={gap}>
        {steps.map((_, index) => {
          const star = 5 - index;
          return (
            <Actionable
              key={`star-label-${star}`}
              onClick={() => handleClick(star)}
              disabled={noReviews}
            >
              <Text
                variant="inline-link"
                decoration="underline"
                weight="medium"
                className={cx({
                  [styles.stars]: pdpBottomRedesign,
                })}
              >
                {star === 1 ? `${star} ${labels.star}` : `${star} ${labels.stars}`}
              </Text>
            </Actionable>
          );
        })}
      </FlexGrid>
      <FlexGrid className={cx(styles.column, styles.grow)} gap={gap}>
        {steps.map((_, index) => {
          const star = 5 - index;
          const rating = ratings.find((rating) => rating.ratingValue === star.toString());
          const average = noReviews ? '0' : (rating?.ratingValuePercentage ?? 0);

          return (
            <Actionable
              key={`${index}-star-rating-graph`}
              onClick={() => handleClick(star)}
              disabled={noReviews}
              attributes={{
                'data-testid': 'distribution',
                'data-stars': star.toString(),
                'data-average': average.toString(),
                'data-noreviews': noReviews,
                'aria-label': `${star} ${star === 1 ? labels.star : labels.stars}, ${average}%`,
              }}
            >
              <FlexGrid
                alignItems="center"
                className={cx(styles.grow, { [styles['--noHeight']]: pdpBottomRedesign })}
                gap={2}
              >
                <Icon
                  svg={StarFill}
                  color={noReviews ? '300' : 'accent'}
                  size={{ s: 6, m: pdpBottomRedesign ? 5 : 7, l: pdpBottomRedesign ? 5 : 7 }}
                />
                <div className={cx(styles.barContainer)}>
                  <span className={styles.bar} style={{ width: `${average}%` }}></span>
                </div>
              </FlexGrid>
            </Actionable>
          );
        })}
      </FlexGrid>
      <FlexGrid
        className={styles.column}
        alignItems={pdpBottomRedesign ? 'start' : 'center'}
        gap={gap}
      >
        {steps.map((_, index) => {
          const star = 5 - index;
          const rating = ratings.find((rating) => rating.ratingValue == `${star}`);
          const average = noReviews ? '0' : (rating?.ratingValuePercentage ?? 0);

          return (
            <Actionable
              key={`${index}-star-rating-graph`}
              onClick={() => handleClick(star)}
              disabled={noReviews}
            >
              <Text
                variant="inline-link"
                decoration="underline"
                weight="medium"
                className={cx({ [styles.percentage]: pdpBottomRedesign })}
                align={'center'}
              >
                {noReviews ? '0%' : `${average}%`}
              </Text>
            </Actionable>
          );
        })}
      </FlexGrid>
    </FlexGrid>
  );
};
